import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { getAuth, viewHeightWithoutAppbar } from 'util/index';
import { selectedFspIdSelector } from 'selectors/fsp';
import {
  load as guestLoanSearchLoad,
  loadHistory,
  loadScheduled,
} from 'model/guestLoanSearch';
import {
  guestLoanSelector,
  selectGuestLoanHistoryTransactions,
  selectGuestLoanHistoryTotalRecords,
  selectGuestLoanHistoryState,
  selectGuestLoanScheduledTransactions,
  selectGuestLoanScheduledTotalRecords,
  selectGuestLoanScheduledState,
} from 'selectors/guestLoanSearch';
import { useScrollState } from 'hooks';
import PayerDetailSectionsProgress from './PayerDetailSectionsProgress';
import GuestTransactionHistoryPanel from './Transactions/GuestTransactionHistoryPanel';
import GuestScheduledTransactionsPanel from './Transactions/GuestScheduledTransactionsPanel';
import GuestProfile from './Profile/GuestProfile';

const useStyles = (addHeight) => {
  return makeStyles((theme) => ({
    container: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingTop: theme.spacing(0.5),
    },
    root: {
      contentVisibility: 'auto',
      minHeight: theme.breakpoints.values.sm / 2,
      overflowY: 'auto',
      ...(addHeight ? viewHeightWithoutAppbar(theme) : null),
    },
  }))();
};

const validGuestLoanType = (value) =>
  value && value !== 'undefined' && value !== 'null' ? value : null;

const withGuestAuth = (action, auth) =>
  action.auth ? action : { ...action, auth };

const GuestPayerDetailSections = ({ addHeight, loanNumber, loanType }) => {
  const classes = useStyles(addHeight);
  const dispatch = useDispatch();
  const ref = useRef();
  const params = useParams();
  const fspId = useSelector(selectedFspIdSelector);
  const auth = useSelector(getAuth);
  const guestLoans = useSelector(guestLoanSelector, shallowEqual);

  const storedLoanNumber = localStorage.getItem('guestLoanNumber');
  const storedLoanType = validGuestLoanType(
    localStorage.getItem('guestLoanType')
  );
  const loanNumberToShow = loanNumber || storedLoanNumber || params.number;
  const storedLoanTypeForLoan =
    storedLoanNumber &&
    loanNumberToShow &&
    String(storedLoanNumber) === String(loanNumberToShow)
      ? storedLoanType
      : null;
  const guestLoan = guestLoans.find(
    (loan) => String(loan.number) === String(loanNumberToShow)
  );
  const loanTypeToShow =
    loanType ||
    storedLoanTypeForLoan ||
    validGuestLoanType(guestLoan?.type) ||
    'Guest';
  const authReady = Boolean(auth?.id);
  const lastLoadedLoanRef = useRef();
  const lastLoadedTransactionsRef = useRef({
    history: undefined,
    scheduled: undefined,
  });
  const pendingTransactionsRef = useRef({
    history: undefined,
    scheduled: undefined,
  });
  const isReload =
    typeof performance !== 'undefined' &&
    (performance.getEntriesByType?.('navigation')?.[0]?.type === 'reload' ||
      performance.navigation?.type === 1);
  const reloadToken = `${loanNumberToShow}|${fspId}|${auth?.id || ''}`;
  const lastReloadTokenRef = useRef();
  const forceReload = isReload && lastReloadTokenRef.current !== reloadToken;

  useEffect(() => {
    lastLoadedLoanRef.current = undefined;
    lastLoadedTransactionsRef.current = {
      history: undefined,
      scheduled: undefined,
    };
  }, [loanNumberToShow, fspId, auth]);

  useEffect(() => {
    if (
      fspId &&
      loanNumberToShow &&
      loanTypeToShow &&
      authReady &&
      !forceReload &&
      lastLoadedLoanRef.current !== loanNumberToShow
    ) {
      dispatch(
        withGuestAuth(
          guestLoanSearchLoad({
            fspId,
            loanNumber: loanNumberToShow,
            loanType: loanTypeToShow,
          }),
          auth
        )
      );
      lastLoadedLoanRef.current = loanNumberToShow;
    }
  }, [
    dispatch,
    fspId,
    loanNumberToShow,
    loanTypeToShow,
    auth,
    authReady,
    forceReload,
  ]);

  const handleTransactionOpen = useCallback(
    (transactionType, loadAction, force = false) => {
      const loadedLoanNumber =
        lastLoadedTransactionsRef.current[transactionType];
      if (!loanNumberToShow || !loanTypeToShow) {
        return;
      }
      if (!authReady) {
        pendingTransactionsRef.current[transactionType] = loanNumberToShow;
        return;
      }
      if (forceReload) {
        return;
      }
      if (fspId && (force || loadedLoanNumber !== loanNumberToShow)) {
        dispatch(
          withGuestAuth(
            loadAction({
              fspId,
              loanNumber: loanNumberToShow,
            }),
            auth
          )
        );
        lastLoadedTransactionsRef.current = {
          ...lastLoadedTransactionsRef.current,
          [transactionType]: loanNumberToShow,
        };
      }
      pendingTransactionsRef.current[transactionType] = undefined;
    },
    [
      auth,
      authReady,
      dispatch,
      forceReload,
      fspId,
      loanNumberToShow,
      loanTypeToShow,
    ]
  );

  const handleHistoryOpen = useCallback(
    (_request, force = false) =>
      handleTransactionOpen('history', loadHistory, force),
    [handleTransactionOpen]
  );

  const handleScheduledOpen = useCallback(
    (_request, force = false) =>
      handleTransactionOpen('scheduled', loadScheduled, force),
    [handleTransactionOpen]
  );

  useEffect(() => {
    if (
      !authReady ||
      !fspId ||
      !loanNumberToShow ||
      !loanTypeToShow ||
      forceReload
    ) {
      return;
    }

    if (pendingTransactionsRef.current.history === loanNumberToShow) {
      handleTransactionOpen('history', loadHistory);
    }
    if (pendingTransactionsRef.current.scheduled === loanNumberToShow) {
      handleTransactionOpen('scheduled', loadScheduled);
    }
  }, [
    authReady,
    forceReload,
    fspId,
    handleTransactionOpen,
    loanNumberToShow,
    loanTypeToShow,
  ]);

  useEffect(() => {
    if (
      forceReload &&
      fspId &&
      loanNumberToShow &&
      loanTypeToShow &&
      authReady
    ) {
      lastReloadTokenRef.current = reloadToken;
      lastLoadedLoanRef.current = loanNumberToShow;
      lastLoadedTransactionsRef.current = {
        history: loanNumberToShow,
        scheduled: loanNumberToShow,
      };
      dispatch(
        withGuestAuth(
          guestLoanSearchLoad({
            fspId,
            loanNumber: loanNumberToShow,
            loanType: loanTypeToShow,
          }),
          auth
        )
      );
      dispatch(
        withGuestAuth(
          loadHistory({ fspId, loanNumber: loanNumberToShow }),
          auth
        )
      );
      dispatch(
        withGuestAuth(
          loadScheduled({ fspId, loanNumber: loanNumberToShow }),
          auth
        )
      );
    }
  }, [
    dispatch,
    fspId,
    loanNumberToShow,
    loanTypeToShow,
    auth,
    authReady,
    forceReload,
    reloadToken,
  ]);

  useEffect(() => {
    if (guestLoan) {
      if (localStorage.getItem('guestLoanNumber') !== guestLoan.number) {
        localStorage.setItem('guestLoanNumber', guestLoan.number);
      }
      if (localStorage.getItem('guestLoanType') !== guestLoan.type) {
        localStorage.setItem('guestLoanType', guestLoan.type);
      }
    }
  }, [guestLoan]);

  const scheduledTransactionsFromRedux = useSelector(
    (state) => selectGuestLoanScheduledTransactions(state, loanNumberToShow),
    shallowEqual
  );
  const historyTransactions = useSelector(
    (state) => selectGuestLoanHistoryTransactions(state, loanNumberToShow),
    shallowEqual
  );
  const historyTotalRecords = useSelector((state) =>
    selectGuestLoanHistoryTotalRecords(state, loanNumberToShow)
  );
  const historyState = useSelector((state) =>
    selectGuestLoanHistoryState(state, loanNumberToShow)
  );
  const scheduledState = useSelector((state) =>
    selectGuestLoanScheduledState(state, loanNumberToShow)
  );
  const scheduledTotalRecords = useSelector((state) =>
    selectGuestLoanScheduledTotalRecords(state, loanNumberToShow)
  );
  const scheduledTransactions = scheduledTransactionsFromRedux;

  useScrollState('pos', ref.current);

  return (
    <div ref={ref} className={classes.root}>
      <PayerDetailSectionsProgress />
      <div className={classes.container}>
        <GuestProfile loanNumber={loanNumberToShow} loanType={loanTypeToShow} />
        <GuestScheduledTransactionsPanel
          items={scheduledTransactions}
          loanNumber={loanNumberToShow}
          fspId={fspId}
          onOpen={handleScheduledOpen}
          loading={scheduledState.loading}
          error={scheduledState.error}
          status={scheduledState.status}
          totalRecords={scheduledTotalRecords}
          authReady={authReady}
        />
        <GuestTransactionHistoryPanel
          items={historyTransactions}
          totalRecords={historyTotalRecords}
          loanNumber={loanNumberToShow}
          fspId={fspId}
          onOpen={handleHistoryOpen}
          loading={historyState.loading}
          error={historyState.error}
          status={historyState.status}
          authReady={authReady}
        />
      </div>
    </div>
  );
};

GuestPayerDetailSections.propTypes = {
  addHeight: PropTypes.bool,
  loanNumber: PropTypes.string,
  loanType: PropTypes.string,
};

GuestPayerDetailSections.defaultProps = {
  addHeight: true,
  loanNumber: null,
  loanType: null,
};

export default GuestPayerDetailSections;
